import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Image } from 'Components/atoms/Image';
import { Price } from 'Components/atoms/Price';
import { Link } from 'Components/molecules/Link';
import { withPolaris } from 'Components/protons/Polaris';
import { asyncTrackLinkClick } from 'SharedHelpers/tracking';

import './_style.scss';

/**
 * CommerceCard component renders a card with product information including image, title, prices, and links.
 */
const CommerceCard = ({
  context: { cssPrefix },
  extraClassNames,
  image: {
    alt,
    height,
    src,
    width,
  },
  marketplaceLink: {
    label: marketplaceLabel,
    tracking,
    url: marketplaceUrl,
  },
  usedMarketplaceLink: {
    label: usedMarketplaceLabel,
    tracking: usedMarketplaceTracking,
    url: usedMarketplaceUrl,
  },
  rrpPrice: {
    min: rrpMin,
    prefixText: rrpPrefixText,
  },
  usedPrice: {
    min: usedMin,
    prefixText: usedPrefixText,
  },
  savingPrice: {
    min: savingMin,
    postText: savingPostText,
    prefixText: savingPrefixText,
  },
  title,
  url,
}) => {
  /**
   * Handles the marketplace link click event.
   * Pushes the tracking object to the dataLayer.
   * @example of tracking object:
   * {
   *  event: 'marketplace.button_click',
   *  'event.type': 'GYCCustom',
   *  'marketplace_button.placement': 'GYCHotDealsBlockCustom',
   *  }
   */
  const handleLinkClick = async (trackingPayload) => {
    if (typeof window !== 'undefined' && tracking && Object.keys(tracking).length > 0) {
      window.dataLayer = window.dataLayer || [];
      await asyncTrackLinkClick(trackingPayload);
    }
  };

  return (
    <div
      className={classNames({
        [`${cssPrefix}__commerce-card`]: true,
        ...extraClassNames,
      })}
    >
      <div className={`${cssPrefix}__commerce-card--media`}>
        <Image
          extraClassNames={{
            [`${cssPrefix}__commerce-card--image`]: true,
          }}
          disableTransforms
          src={src}
          alt={alt}
          height={height}
          width={width}
        />
      </div>
      <div className={`${cssPrefix}__commerce-card--content`}>
        <div className={`${cssPrefix}__commerce-card--info`}>
          <p className={`${cssPrefix}__commerce-card--title`}>
            <Link
              href={url}
            >
              {title}
            </Link>
          </p>
          {rrpMin > 0 && (
            <Price
              extraClassNames={{
                [`${cssPrefix}__commerce-card--rrp-price`]: true,
              }}
              minOnly
              min={rrpMin}
              prefixText={rrpPrefixText}
            />
          )}
          {savingMin > 0 && (
            <Price
              extraClassNames={{
                [`${cssPrefix}__commerce-card--saving`]: true,
              }}
              minOnly
              min={savingMin}
              prefixText={savingPrefixText}
              postText={savingPostText}
            />
          )}
          {usedMin && usedMin > 0 && (
            <Price
              extraClassNames={{
                [`${cssPrefix}__commerce-card--used-price`]: true,
              }}
              minOnly
              min={usedMin}
              prefixText={usedPrefixText}
            />
          )}
        </div>
        <div className={`${cssPrefix}__commerce-card--ctas`}>
          {rrpMin && rrpMin > 0 && marketplaceUrl && (
            <Link
              extraClassNames={{
                [`${cssPrefix}__commerce-card--marketplace-link`]: true,
              }}
              href={marketplaceUrl}
              onClick={async () => { await handleLinkClick(tracking); }}

            >
              {marketplaceLabel}
            </Link>
          )}
          {usedMin && usedMin > 0 && usedMarketplaceUrl && (
            <Link
              extraClassNames={{
                [`${cssPrefix}__commerce-card--used-link`]: true,
              }}
              href={usedMarketplaceUrl}
              onClick={async () => { await handleLinkClick(usedMarketplaceTracking); }}
            >
              {usedMarketplaceLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

CommerceCard.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.shape({}),
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.number,
    src: PropTypes.string,
    width: PropTypes.number,
  }),
  marketplaceLink: PropTypes.shape({
    label: PropTypes.string,
    tracking: PropTypes.shape(),
    url: PropTypes.string,
  }),
  usedMarketplaceLink: PropTypes.shape({
    label: PropTypes.string,
    tracking: PropTypes.shape(),
    url: PropTypes.string,
  }),
  rrpPrice: PropTypes.shape({
    min: PropTypes.number,
    prefixText: PropTypes.string,
  }),
  usedPrice: PropTypes.shape({
    min: PropTypes.number,
    prefixText: PropTypes.string,
  }),
  savingPrice: PropTypes.shape({
    min: PropTypes.number,
    postText: PropTypes.string,
    prefixText: PropTypes.string,
  }),
  title: PropTypes.string,
  url: PropTypes.string,
};

CommerceCard.defaultProps = {
  context: { cssPrefix: 'polaris' },
  extraClassNames: {},
  image: {
    alt: '',
    height: 0,
    src: '',
    width: 0,
  },
  marketplaceLink: {
    label: '',
    tracking: {},
    url: '',
  },
  usedMarketplaceLink: {
    label: '',
    tracking: {},
    url: '',
  },
  rrpPrice: {
    min: 0,
    prefixText: '',
  },
  usedPrice: {
    min: 0,
    prefixText: '',
  },
  savingPrice: {
    min: 0,
    postText: '',
    prefixText: '',
  },
  title: '',
  url: '',
};

export default withPolaris(CommerceCard);
